import type { MyPresenceDto, PresenceTypeDto } from '@dijitalapp/shared';
import { useEffect, useSyncExternalStore } from 'react';
import { api } from './api';

/* My own status (a status from the panel's list, or automatic) and the list I may pick from. */

let mine: MyPresenceDto | null = null;
let types: PresenceTypeDto[] | null = null;
let loading = false;
let loadingTypes = false;
const listeners = new Set<() => void>();
let timer: ReturnType<typeof setTimeout> | null = null;
const emit = () => listeners.forEach((l) => l());

function set(next: MyPresenceDto) {
  mine = next;
  if (timer) clearTimeout(timer);
  // A status with an end goes back to automatic by itself: reload when it ends.
  const left = next.until ? Date.parse(next.until) - Date.now() : 0;
  if (left > 0) timer = setTimeout(() => void loadMyPresence(true), Math.min(left + 1000, 2 ** 31 - 1));
  emit();
}

export function loadMyPresence(force = false): Promise<void> {
  if ((mine && !force) || loading) return Promise.resolve();
  loading = true;
  return api<MyPresenceDto>('GET', '/me/presence')
    .then(set, () => undefined)
    .finally(() => {
      loading = false;
    });
}

/** The statuses I may pick (asked again each time the picker opens: the panel may have changed them). */
export function loadPresenceTypes(): Promise<void> {
  if (loadingTypes) return Promise.resolve();
  loadingTypes = true;
  return api<{ types: PresenceTypeDto[] }>('GET', '/me/presence/types')
    .then((r) => {
      types = r.types;
      emit();
    }, () => undefined)
    .finally(() => {
      loadingTypes = false;
    });
}

/** Picks a status from the list (null = automatic), until a time or until changed. */
export async function setMyPresence(typeId: string | null, until: string | null): Promise<void> {
  set(await api<MyPresenceDto>('PUT', '/me/presence', { typeId, until }));
}

/** Do not disturb right now (no message sounds or notifications on this device either). */
export function doNotDisturb(): boolean {
  return mine?.choice === 'dnd' && (!mine.until || Date.parse(mine.until) > Date.now());
}

function subscribe(l: () => void) {
  listeners.add(l);
  return () => listeners.delete(l);
}

export function useMyPresence(): MyPresenceDto | null {
  const value = useSyncExternalStore(subscribe, () => mine);
  useEffect(() => {
    void loadMyPresence();
  }, []);
  return value;
}

export function usePresenceTypes(): PresenceTypeDto[] | null {
  const value = useSyncExternalStore(subscribe, () => types);
  useEffect(() => {
    void loadPresenceTypes();
  }, []);
  return value;
}
